import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { claimActions, httpRequestsOnSuccessActions } from 'store';
import { Colors, DownloadFile, FindLoad, FindSuccessItem, Images, PermissionList, RolePermission, useModal } from 'utils';
import {
    AccordionFullTable,
    CustomDelByNote,
    CustomDelete,
    FullTable,
    MarkSecondaryBilling,
    MinLoader,
    RemoveClaim,
    SelectActions,
} from 'components';
import { submittedBatchTableInfo, submittedClaimTableInfo, tableInfoToFullTable } from './constants';
import { ReceivableBillTable } from 'fragments';
import { ResubmitClaim, ClaimAdjustment, SubmittedClaimsAging, ChangeClaimGroupedStatus, ManageSecondaryBilling } from './core';
import { secondaryStatusEnums } from '../claimPayment/constants';

const CHANGE_STATUS_ACTION = 'CHANGE_CLAIM_STATUS';
const CANCEL_CLAIM = 'CANCEL_CLAIM';
const CHECK_CLAIM_STATUS = 'CHECK_CLAIM_STATUS';
const GET_CLAIM_BATCH_BY_ID = 'GET_CLAIM_BATCH_BY_ID';

export const SubmittedClaim = () => {
    const submittedClaims = useSelector((state) => state.claim.claims);
    const claimBatches = useSelector((state) => state.claim.claimBatches);
    const claimBatchById = useSelector((state) => state.claim.claimBatchById);
    const canUseIntegrations = useSelector((state) => state.system.canUseIntegrations);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const info = location?.state;
    const batchView = !!info?.batchView;
    const { open, close } = useModal();
    const [selectedList, setSelectedList] = useState([]);
    const [expandedBatchId, setExpandedBatchId] = useState(null);
    const cancelClaimsLoader = FindLoad(CANCEL_CLAIM);
    const batchClaimsLoader = FindLoad(GET_CLAIM_BATCH_BY_ID);
    const updateStatusesSuccess = FindSuccessItem(CHANGE_STATUS_ACTION);
    const { loadDownload, handleDownload, currentId } = DownloadFile();

    const renderParams = () => {
        const sendInfo = {
            ...info,
            status: info?.status ? info?.status : 'SUBMITTED',
        };

        delete sendInfo.page;
        delete sendInfo.tabType;
        delete sendInfo.clientFull;
        // Display-only companion for `payer` — forwarding it sends the payer's NAME as a
        // query param alongside its id. billableAppt.js already stripped it; these did not.
        delete sendInfo.funderName;
        delete sendInfo.batchView;
        sendInfo.skip = info?.skip ? info?.skip : 0;
        sendInfo.limit = 50;

        if (info?.batchView) {
            delete sendInfo.status;
        }
        return sendInfo;
    };

    const getSubmittedClaims = (loading) => {
        if (batchView) {
            dispatch(claimActions.getClaimBatches({ ...renderParams() }, loading));
            if (expandedBatchId) {
                dispatch(claimActions.getClaimBatchById(expandedBatchId, loading));
            }
        } else {
            dispatch(claimActions.getPendingClaims({ ...renderParams() }, loading));
        }

        const agingParams = {
            ...renderParams(),
        };
        delete agingParams.skip;
        delete agingParams.limit;
        delete agingParams.agingType;
        dispatch(claimActions.getClaimAging({ ...agingParams }, loading));
        dispatch(claimActions.getPendingClaimsTotal(agingParams, loading));
    };

    useEffect(() => {
        if (updateStatusesSuccess) {
            close();
            removeSelectedList();
            dispatch(httpRequestsOnSuccessActions.removeSuccess(updateStatusesSuccess));
            getSubmittedClaims('noLoad');
        }
    }, [updateStatusesSuccess]);

    useEffect(() => {
        getSubmittedClaims();
    }, [info]);

    const handleCheck = (ev, item, name) => {
        console.log(name, 'name');
        if (name === 'all' || name === 'selectAll') {
            if(batchView){
                setSelectedList(claimBatchById?.claims?.length === selectedList?.length ? [] : claimBatchById?.claims?.map((i) => i?.id));
            }else{
                setSelectedList(submittedClaims?.claims?.length === selectedList?.length ? [] : submittedClaims?.claims?.map((i) => i?.id));

            }
        } else {
            setSelectedList((prev) => (prev.includes(item?.id) ? prev.filter((id) => id !== item?.id) : [...prev, item?.id]));
        }
    };

    const batchClaimIds = (batch) => (batch?.claims || []).map((c) => (typeof c === 'string' ? c : c?.id)).filter(Boolean);

    const handleBatchCheck = (batch) => {
        const ids = batchClaimIds(batch);
        if (!ids.length) return;
        const allSelected = ids.every((id) => selectedList?.includes(id));
        setSelectedList((prev) => (allSelected ? prev.filter((id) => !ids.includes(id)) : Array.from(new Set([...prev, ...ids]))));
    };

    const handleBatchExpand = (panel, batch) => {
        removeSelectedList();

        if (panel !== false && batch?.id) {
            setExpandedBatchId(batch.id);
            dispatch(claimActions.getClaimBatchById(batch.id));
        } else {
            setExpandedBatchId(null);
        }
    };

    const removeSelectedList = () => {
        setSelectedList([]);
    };

    const updateStatus = () => {
        open(
            <ChangeClaimGroupedStatus
                ids={selectedList}
                afterSuccess={() => {
                    getSubmittedClaims('noLoad');
                    removeSelectedList();
                }}
            />
        );
    };

    const checkStatus = () => {
        open(
            <CustomDelete
                info={'Real-Time Claim Status (276/277)'}
                text={
                    'The system will check the latest status of the selected claims using real-time 276/277 transactions. Claim statuses will be updated automatically when responses are received.'
                }
                handleDel={() => dispatch(claimActions.checkClaimStatus({ claimIds: selectedList }))}
                actionType={CHECK_CLAIM_STATUS}
                afterSuccess={() => {
                    getSubmittedClaims('noLoad');
                    removeSelectedList();
                }}
                innerText="Check Status"
                color={'blue'}
            />
        );
    };

    const cancelClaims = () => {
        open(
            <CustomDelete
                info="Cancel Claim?"
                text="This action will cancel the submitted claim and move all related appts back to the Billable appts section."
                handleDel={() => dispatch(claimActions.cancelClaim({ claimIds: selectedList }))}
                actionType={CANCEL_CLAIM}
                afterSuccess={() => {
                    getSubmittedClaims('noLoad');
                    removeSelectedList();
                }}
                innerText="Confirm Cancel"
                body={
                    <div className="clos-claims-info-box">
                        <img src={Images.infoCircle} alt="info" />
                        <p>Accepted claims cannot be canceled during multiple cancellation.</p>
                    </div>
                }
            />
        );
    };

    const handleAction = (item, name) => {
        if (name === 'appeal') {
            open(
                <CustomDelByNote
                    info="Appeal this claim?"
                    text="Please provide a reason for appealing this claim."
                    handleDel={(closingComment) =>
                        dispatch(
                            claimActions.appealSubmittedClaim(
                                {
                                    id: item?.id,
                                    mark: true,
                                    description: closingComment,
                                },
                                renderParams()
                            )
                        )
                    }
                    afterSuccess={() => {
                        getSubmittedClaims('noLoad');
                        removeSelectedList();
                    }}
                    innerText={'Appeal'}
                    inputLabel={'Add your comment here ...*'}
                    actionType={'APPEAL_SUBMITTED_CLAIM'}
                />
            );
        }
        if (name === 'removeFromClaim') {
            if (RolePermission([PermissionList.CLAIM_PMT_SEND_APPEAL?.code])) {
                open(<RemoveClaim claim={item} noBtn={true} afterSuccess={() => getSubmittedClaims('noLoad')} />);
            }
        }
        if (name === 'secondaryBilling') {
            if (
                // item?.secondaryBilling?.secondaryBillingState === secondaryStatusEnums.READY &&
                !item?.secondaryBilling
            ) {
                open(
                    <MarkSecondaryBilling
                        afterSuccess={() => getSubmittedClaims('noLoad')}
                        mark={!item?.secondaryBilling?.secondaryBillingState}
                        claim={item}
                    />
                );
            } else if (item?.secondaryBilling?.secondaryBillingState === secondaryStatusEnums.READY && !item?.secondaryBilling?.submitted) {
                open(<ManageSecondaryBilling claim={item} afterSuccess={() => getSubmittedClaims('noLoad')} />);
            } else {
                open(
                    <CustomDelete
                        text={'Are you sure you want to unmark this claim?'}
                        info={'Unmark Secondary Billing'}
                        innerText={'Unmark Claim'}
                        color={'blue'}
                        handleDel={() => dispatch(claimActions.markAsSecondaryBilling(item?.id, { mark: false }))}
                        afterSuccess={() => {
                            close();
                            getSubmittedClaims('noLoad');
                        }}
                        actionType={'MARK_AS_SECONDARY_BILLING'}
                        body={
                            <div>
                                <div className="deleting-client-info-box" style={{ alignItems: 'flex-start', marginBottom: 12 }}>
                                    <img style={{ marginTop: '3px' }} src={Images.receipt} alt="icon" />
                                    <p style={{ fontWeight: 400, color: '#0C66E4' }}>{item?.displayId}</p>
                                </div>
                            </div>
                        }
                    />
                );
            }
        }
        // if (name === 'adjustment') {
        //     open(<ClaimAdjustment item={item} afterSuccess={() => getSubmittedClaims('noLoad')} />);
        // }
    };

    return (
        <div>
            {!!selectedList?.length && (
                <SelectActions
                    submitText={'Resubmit'}
                    submitBtnIcon={Images.revertWhite}
                    selectedList={selectedList?.length}
                    selectInnetText={selectedList?.length === submittedClaims?.claims?.length ? 'Clear Selection' : 'Select All'}
                    selectAll={handleCheck}
                    removeSelected={removeSelectedList}
                    renderOtherBtns={
                        <div className="flex-align-center" style={{ gap: 8 }}>
                            <button onClick={updateStatus} className="select-all-btn">
                                <img src={Images.updateStatuses} alt="show" />
                                <p>Update Status</p>
                            </button>

                            {canUseIntegrations?.claimStatusCheckEnabled && (
                                <button onClick={checkStatus} className="select-all-btn">
                                    <img src={Images.verified} alt="show" />
                                    <p>Check Status</p>
                                </button>
                            )}

                            {/*{RolePermission([PermissionList.UN_SUBMIT_CLAIM_APPT?.code]) &&*/}
                            <button disabled={!!cancelClaimsLoader?.length} onClick={cancelClaims} className="select-all-btn">
                                <img style={{ color: '#FF938B' }} src={Images.remove} alt="show" />
                                <p style={{ color: '#FF938B' }}>Cancel</p>
                            </button>
                            {/*}/*/}
                        </div>
                    }
                    submitClaims={() =>
                        open(
                            <ResubmitClaim
                                selectedList={selectedList}
                                afterSuccess={() => {
                                    removeSelectedList();
                                }}
                            />
                        )
                    }
                />
            )}

            <SubmittedClaimsAging />
            {/*<Aging aging={aging?.claims} />*/}

            {batchView ? (
                <AccordionFullTable
                    margin={'6px 0 0 0'}
                    minWidth={1100}
                    tableInfo={submittedBatchTableInfo(handleBatchCheck, selectedList, loadDownload, handleDownload, currentId)}
                    list={claimBatches?.claimBatches}
                    listCount={claimBatches?.count}
                    handleAccordion={handleBatchExpand}
                    renderAccordionDetails={(batch, isExpanded) => {
                        const batchClaims = claimBatchById?.claims
                        const isLoadingThisBatch = isExpanded && expandedBatchId === batch?.id && !!batchClaimsLoader?.length;

                        if (isLoadingThisBatch) {
                            return (
                                <div className="flex-align-center" style={{ justifyContent: 'center', padding: 24 }}>
                                    <MinLoader margin={'0'} position={'relative'} />
                                </div>
                            );
                        }

                        const claimsList = batchClaims || [];
                        const { head, body } = tableInfoToFullTable(submittedClaimTableInfo(navigate, info, handleCheck, handleAction, batchClaims, selectedList, getSubmittedClaims, batchView), claimsList);

                        return (
                            <FullTable
                                head={head}
                                body={body}
                                list={batchClaims}
                                accordionBody={true}
                                minWidth={1750}
                                loadingType={isLoadingThisBatch}
                                height={'extraSmall'}
                                activeRowId={''}
                                margin={'0'}
                                selectedList={selectedList}
                                notYet={{
                                    title: 'No Claims in Batch',
                                    subTitle: 'There are no claims in this submission batch.',
                                    image: Images.noPendingClaim,
                                }}
                            />
                        );
                    }}
                    selectedList={selectedList}
                />
            ) : (
                <AccordionFullTable
                    margin={'6px 0 0 0'}
                    minWidth={1750}
                    tableInfo={submittedClaimTableInfo(
                        navigate,
                        info,
                        handleCheck,
                        handleAction,
                        submittedClaims?.claims,
                        selectedList,
                        getSubmittedClaims
                    )}
                    list={submittedClaims?.claims}
                    listCount={submittedClaims?.count}
                    onRowClick={(item, name) => {
                        open(<ReceivableBillTable defaultBill={item} />);
                    }}
                    selectedList={selectedList}
                />
            )}
        </div>
    );
};
